import React, { useEffect, useRef, useState } from 'react';

import { CLEF_CENTER_MIDI, TIME_SIGNATURES } from '../config/music';
import { ClefType, Note, TimeSignature } from '../types';

import { DetectedGhost } from './staff/DetectedGhost';
import { ExitingNotes } from './staff/ExitingNotes';
import { GrandStaffCanvas } from './staff/GrandStaffCanvas';
import { StaffHeader } from './staff/StaffHeader';
import { createStaffLayout, FALLBACK_VIEWPORT_WIDTH } from './staff/staffLayout';
import { StaffLines } from './staff/StaffLines';
import { NoteLayout, StaffNotes } from './staff/StaffNotes';

interface StaffDisplayProps {
  mode?: 'single' | 'grand';
  clef: ClefType;
  noteQueue: Note[];
  exitingNotes: Note[];
  detectedNote: Note | null;
  status: 'waiting' | 'listening' | 'correct' | 'incorrect';
  micLabel: string;
}

type StaffCanvasProps = {
  clef: ClefType;
  noteQueue: Note[];
  exitingNotes: Note[];
  detectedNote: Note | null;
  viewportWidth: number;
  timeSignature: TimeSignature;
};

const StaffCanvas: React.FC<StaffCanvasProps> = ({
  clef,
  noteQueue,
  exitingNotes,
  detectedNote,
  viewportWidth,
  timeSignature,
}) => {
  const layout = createStaffLayout(viewportWidth);
  const centerMidi = CLEF_CENTER_MIDI[clef];
  const availableWidth = Math.max(0, layout.VIEWPORT_WIDTH - layout.START_X - layout.RIGHT_PADDING);
  const maxFitNotes = Math.max(1, Math.floor(availableWidth / layout.NOTE_SPACING) + 1);
  const visibleCount = Math.min(noteQueue.length, maxFitNotes);
  const contentWidth =
    layout.START_X + Math.max(visibleCount - 1, 0) * layout.NOTE_SPACING + layout.RIGHT_PADDING;

  const layoutNotes: NoteLayout[] = noteQueue.slice(0, visibleCount).map((note, index) => ({
    note,
    index,
    x: layout.START_X + index * layout.NOTE_SPACING,
  }));

  return (
    <svg
      width={contentWidth}
      height={layout.SVG_HEIGHT}
      viewBox={`0 0 ${contentWidth} ${layout.SVG_HEIGHT}`}
      className="block max-w-none"
      style={{ width: `${contentWidth}px`, height: `${layout.SVG_HEIGHT}px` }}
      preserveAspectRatio="xMinYMin meet"
    >
      <rect
        x={layout.HIGHLIGHT_X}
        y={layout.STAFF_SPACE}
        width={layout.HIGHLIGHT_WIDTH}
        height={layout.SVG_HEIGHT - layout.STAFF_SPACE * 2}
        fill="var(--color-staff-highlight)"
        rx="8"
      />
      <StaffHeader clef={clef} layout={layout} timeSignature={timeSignature} />
      <StaffLines layout={layout} contentWidth={contentWidth} />
      <StaffNotes
        layoutNotes={layoutNotes}
        noteQueue={noteQueue}
        detectedNote={detectedNote}
        activeNote={noteQueue[0]}
        centerMidi={centerMidi}
        layout={layout}
        timeSignature={timeSignature}
      />
      <ExitingNotes
        exitingNotes={exitingNotes}
        centerMidi={centerMidi}
        layout={layout}
        timeSignature={timeSignature}
      />
      <DetectedGhost
        detectedNote={detectedNote}
        activeNote={noteQueue[0]}
        centerMidi={centerMidi}
        layout={layout}
      />
    </svg>
  );
};

const StaffDisplay: React.FC<StaffDisplayProps> = ({
  mode = 'single',
  clef,
  noteQueue,
  exitingNotes,
  detectedNote,
  status,
  micLabel,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateWidth = () => setContainerWidth(el.getBoundingClientRect().width);
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  const viewportWidth = containerWidth || FALLBACK_VIEWPORT_WIDTH;
  const timeSignature = TIME_SIGNATURES['4/4'];

  return (
    <div
      ref={containerRef}
      data-testid="staff-display"
      className="w-full bg-white dark:bg-slate-900 rounded-xl relative overflow-hidden select-none border border-slate-200 dark:border-slate-800 shadow-sm"
    >
      {mode === 'grand' ? (
        <GrandStaffCanvas
          noteQueue={noteQueue}
          exitingNotes={exitingNotes}
          detectedNote={detectedNote}
          viewportWidth={viewportWidth}
          timeSignature={timeSignature}
        />
      ) : (
        <StaffCanvas
          clef={clef}
          noteQueue={noteQueue}
          exitingNotes={exitingNotes}
          detectedNote={detectedNote}
          viewportWidth={viewportWidth}
          timeSignature={timeSignature}
        />
      )}
      {status === 'listening' && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-full border border-slate-200 dark:bg-slate-800 dark:border-slate-700">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wide dark:text-slate-300">
            {micLabel}
          </span>
        </div>
      )}
    </div>
  );
};

export default StaffDisplay;
